/**
 * VÉRAULT Portal — Route tree.
 * Wraps every /portal/* page in the new PortalShell so the shell is
 * mounted once per role rather than re-rendering on each navigation.
 *
 * Naming refinement (Feb 2026):
 *   Atelier → Overview
 *   Trips    → Journeys
 *   Messages → Concierge (now includes AI tools)
 *   + Planning, Insights, Journal, Documents, Travelers, client Payments
 */
import React, { Suspense } from "react";
import { Routes, Route, Navigate, Outlet, Link, useParams as useRouterParams } from "react-router-dom";
import { PortalShell } from "./shell/PortalShell";
import { PortalButton } from "./components/Button";

// Advisor pages
import AdvisorOverview from "./pages/advisor/Atelier"; // file kept; UI now says "Overview"
import AdvisorClients from "./pages/advisor/Clients";
import AdvisorClientProfile from "./pages/advisor/ClientProfile";
import AdvisorJourneys from "./pages/advisor/Trips"; // file kept; UI says "Journeys"
// Wave 30.6 · Phase 11c — Lazy-load heavy pages.
// JourneyDetail mounts ItineraryEditor (2k+ lines), 25+ shadcn dialogs,
// and the whole revision-deck UI. It's only opened when an advisor
// selects a journey — perfect lazy-split candidate. Keeps the initial
// /portal/advisor bundle ~120kB lighter.
const AdvisorJourneyDetail = React.lazy(() => import("./pages/advisor/JourneyDetail"));
import ClientPortalPreview from "./pages/advisor/ClientPortalPreview";
import AdvisorBookings from "./pages/advisor/Bookings";
import AdvisorPayments from "./pages/advisor/Payments";
import AdvisorPlanning from "./pages/advisor/Planning";
import AdvisorCalendar from "./pages/advisor/Calendar";
import AdvisorConcierge from "./pages/advisor/Concierge";
import AdvisorAgreements from "./pages/advisor/Agreements";
import AdvisorForms from "./pages/advisor/Forms";
import AdvisorSettings from "./pages/advisor/Settings";
import NotificationsSettings from "./pages/NotificationsSettings";
import TestDataCleanup from "./pages/advisor/TestDataCleanup";
import AdvisorTrash from "./pages/advisor/Trash";
import TripRequestsInbox from "./pages/advisor/TripRequestsInbox";
import AdvisorRecords from "./pages/advisor/Records";
import MyCollection from "./pages/advisor/MyCollection";
import AdvisorFiles from "./pages/advisor/Files";
import AdvisorTreasury from "./pages/advisor/Treasury";
import HouseIntelligence from "./pages/advisor/HouseIntelligence";

// Content Studio (new editorial publishing suite)
import ContentLayout from "./pages/advisor/content/ContentLayout";
import ContentDashboard from "./pages/advisor/content/Dashboard";
import ContentJournal from "./pages/advisor/content/Journal";
import ContentGuides from "./pages/advisor/content/Guides";
import ContentCalendar from "./pages/advisor/content/Calendar";
import ContentMedia from "./pages/advisor/content/Media";
import ContentSocial from "./pages/advisor/content/Social";
import ContentCampaigns from "./pages/advisor/content/Campaigns";
import ContentAssistant from "./pages/advisor/content/Assistant";
import WebsiteEditorList from "./pages/advisor/content/WebsiteEditorList";
// Wave 30.6 · Phase 11c — Website editor is heavy (live preview iframe
// + form binding + media picker). Lazy-split same reason as above.
const WebsiteEditorPage = React.lazy(() => import("./pages/advisor/content/WebsiteEditorPage"));
const ContentEditorPage = React.lazy(() => import("./pages/advisor/content/ContentEditorPage"));
// Wave 34 — slugs that use the field-level content-override editor
// (canonical React layout + per-field overrides). Slugs not in this
// set still use the legacy block-based editor for now.
const CONTENT_OVERRIDE_SLUGS = new Set(["home", "about", "services", "destinations", "journal", "plan"]);
function WebsiteEditorRouter() {
  const { slug } = useRouterParams();
  if (CONTENT_OVERRIDE_SLUGS.has(slug)) {
    return (
      <Lazy>
        <ContentEditorPage />
      </Lazy>
    );
  }
  return (
    <Lazy>
      <WebsiteEditorPage />
    </Lazy>
  );
}

// Client pages
import ClientJourney from "./pages/client/Journey";
import ClientHome from "./pages/client/Home";
import ClientExperiences from "./pages/client/Experiences";
import ClientTripHome from "./pages/client/TripHome";
import ClientItinerary from "./pages/client/Itinerary";
import ClientConcierge from "./pages/client/Concierge";
import ClientDocuments from "./pages/client/Documents";
import ClientTravelers from "./pages/client/Travelers";
import ClientPayments from "./pages/client/Payments";
import ClientSettings from "./pages/client/Settings";
import ClientSchedule from "./pages/client/Schedule";
import ClientConnect from "./pages/client/Connect";
import ClientReadiness from "./pages/client/Readiness";
import ClientPacking from "./pages/client/Packing";// Wave 26.21 Phase 2a — Experience microsite
import ExperienceLayout from "./pages/client/experience/ExperienceLayout";
import ExperienceOverview from "./pages/client/experience/Overview";
import ExperienceItinerary from "./pages/client/experience/Itinerary";
import ExperienceEssentials from "./pages/client/experience/Essentials";
import ExperienceTravelPrep from "./pages/client/experience/TravelPrep";
const ExperienceProposal = React.lazy(() => import("./pages/client/experience/ProposalRouter"));
// Wave 30.6 · Phase 11c — Memories has react-intersection-observer +
// infinite-scroll grid + image lightbox. Lazy-split for /client/* perf.
const ExperienceMemories = React.lazy(() => import("./pages/client/experience/Memories"));
const ExperienceWelcomeHome = React.lazy(() => import("./pages/client/experience/WelcomeHome"));

// Wave 30.6 · Phase 11c — Editorial loading whisper for lazy routes.
// Calm + identical to the page's empty-state aesthetic — never a spinner.
function RouteLoading() {
  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-6"
      data-testid="route-loading"
    >
      <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-muted))]">
        A quiet moment.
      </p>
    </div>
  );
}

function Lazy({ children }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function AdvisorShell() {
  return (
    <PortalShell role="advisor">
      <Outlet />
    </PortalShell>
  );
}

function ClientShell() {
  return (
    <PortalShell role="client">
      {/* Wave 12.t — Auto-prompt for push notifications on first client portal visit. */}
      <ClientPushAutoPrompt />
      <Outlet />
    </PortalShell>
  );
}

// Tiny non-rendering component — sits inside the shell and lazily
// invites the client to enable push notifications. It NEVER auto-
// fires the browser prompt (that would be hostile); it surfaces a
// calm one-time banner toast instead, and only when the device
// actually supports push (i.e., not on dev hosts / unsupported
// browsers). The user's choice is sticky via localStorage.
function ClientPushAutoPrompt() {
  return null;  // banner lives inside Home.jsx — kept here for layout symmetry
}

// ---- Deferred placeholders ----------------------------------------
// (Client Settings now has a real implementation — see pages/client/Settings.jsx)

// Wave 26.21 Phase 2a — preserve old `/trip/:id/...` style URLs by
// redirecting into the new canonical /experiences/:id tree.
import { useParams as _useParams } from "react-router-dom";
function LegacyRedirect({ to }) {
  const { tripId } = _useParams();
  const target = to
    ? `/portal/client/experiences/${tripId}/${to}`
    : `/portal/client/experiences/${tripId}`;
  return <Navigate to={target} replace />;
}

export function AdvisorPortalRoutes() {
  return (
    <Routes>
      {/* Client-portal preview — full-screen, no advisor chrome */}
      <Route path="journeys/:tripId/client-preview/*" element={<ClientPortalPreview />} />
      <Route element={<AdvisorShell />}>
        <Route index element={<AdvisorOverview />} />
        <Route path="clients" element={<AdvisorClients />} />
        <Route path="clients/:clientId" element={<AdvisorClientProfile />} />

        {/* Journeys (was: trips). Old path kept as redirect for muscle memory. */}
        <Route path="journeys" element={<AdvisorJourneys />} />
        <Route path="journeys/:tripId" element={<Lazy><AdvisorJourneyDetail /></Lazy>} />
        <Route path="trips" element={<Navigate to="/portal/advisor/journeys" replace />} />

        {/* Experiences — new canonical surface label, journeys URLs stay alive. */}
        <Route path="experiences" element={<AdvisorJourneys />} />
        <Route path="experiences/:tripId" element={<Lazy><AdvisorJourneyDetail /></Lazy>} />
        <Route path="experiences/:tripId/client-preview/*" element={<ClientPortalPreview />} />

        <Route path="planning" element={<AdvisorPlanning />} />
        <Route path="calendar" element={<AdvisorCalendar />} />
        <Route path="bookings" element={<AdvisorBookings />} />
        <Route path="payments" element={<AdvisorPayments />} />

        {/* Concierge subsumes the old "messages" — kept as redirect. */}
        <Route path="concierge" element={<AdvisorConcierge />} />
        <Route path="messages" element={<Navigate to="/portal/advisor/concierge" replace />} />

        <Route path="forms" element={<AdvisorForms />} />
        <Route path="agreements" element={<AdvisorAgreements />} />
        <Route path="trip-requests" element={<TripRequestsInbox />} />
        {/* Insights folded into Overview as widgets — keep route alive as a redirect */}
        <Route path="insights" element={<Navigate to="/portal/advisor" replace />} />

        {/* Journal CMS — list inside the new portal. The editor at
            /advisor/content/journal/:id renders full-bleed (its own chrome)
            and returns back to /portal/advisor/journal on save/cancel. */}
        <Route path="journal" element={<Navigate to="/portal/advisor/content/journal" replace />} />

        {/* Content Studio — editorial publishing suite */}
        <Route path="content" element={<ContentLayout />}>
          <Route index element={<ContentDashboard />} />
          <Route path="journal" element={<ContentJournal />} />
          <Route path="guides" element={<ContentGuides />} />
          <Route path="calendar" element={<ContentCalendar />} />
          <Route path="media" element={<ContentMedia />} />
          <Route path="social" element={<ContentSocial />} />
          <Route path="campaigns" element={<ContentCampaigns />} />
          <Route path="assistant" element={<ContentAssistant />} />
          {/* Website CMS / Site Editor */}
          <Route path="website" element={<WebsiteEditorList />} />
          <Route path="website/:slug" element={<WebsiteEditorRouter />} />
        </Route>

        <Route path="settings" element={<AdvisorSettings />} />
        <Route path="settings/notifications" element={<NotificationsSettings />} />
        <Route path="settings/test-data-cleanup" element={<TestDataCleanup />} />
        <Route path="settings/trash" element={<AdvisorTrash />} />
        <Route path="records" element={<AdvisorRecords />} />
        <Route path="library" element={<MyCollection />} />
        <Route path="files" element={<AdvisorFiles />} />
        <Route path="intelligence" element={<HouseIntelligence />} />
        <Route path="treasury" element={<AdvisorTreasury />} />
        <Route path="*" element={<Navigate to="/portal/advisor" replace />} />
      </Route>
    </Routes>
  );
}

export function ClientPortalRoutes() {
  return (
    <Routes>
      <Route element={<ClientShell />}>
        {/* Wave 26.20 Phase 1a — Home is now the editorial overview;
            Experiences is the gallery shelf. The old "Journey" page
            (a flat trips index) is kept reachable at /journey/ for
            anyone with the old bookmark, but never linked from the
            sidebar anymore. */}
        <Route index element={<ClientHome />} />
        <Route path="experiences" element={<ClientExperiences />} />
        <Route path="journey" element={<Navigate to="/portal/client" replace />} />
        <Route path="journeys" element={<Navigate to="/portal/client/experiences" replace />} />

        {/* Wave 26.21 Phase 2a — Each Experience opens as a private
            editorial microsite with its own section nav (Overview ·
            Itinerary · Essentials · Travel Prep · Memories). The
            shared `ExperienceLayout` wraps every section so the
            breadcrumb + state chip + section pills feel continuous. */}
        <Route path="experiences/:tripId" element={<ExperienceLayout />}>
          <Route index element={<ExperienceOverview />} />
          <Route path="proposal" element={<Lazy><ExperienceProposal /></Lazy>} />
          <Route path="itinerary" element={<ExperienceItinerary />} />
          <Route path="itinerary/day/:dayNumber" element={<ExperienceItinerary />} />
          <Route path="essentials" element={<ExperienceEssentials />} />
          <Route path="travel-prep" element={<ExperienceTravelPrep />} />
          <Route path="memories" element={<Lazy><ExperienceMemories /></Lazy>} />
          <Route path="welcome" element={<Lazy><ExperienceWelcomeHome /></Lazy>} />
          <Route path="readiness" element={<ClientReadiness />} />
          <Route path="packing" element={<ClientPacking />} />
        </Route>

        {/* Legacy aliases — preserved so old deep links + advisor
            previews keep working. They all redirect into the new
            canonical /experiences/:id tree. */}
        <Route path="trip/:tripId" element={<LegacyRedirect to="" />} />
        <Route path="trip/:tripId/itinerary" element={<LegacyRedirect to="itinerary" />} />
        <Route path="trip/:tripId/readiness" element={<LegacyRedirect to="readiness" />} />
        <Route path="journey/:tripId" element={<LegacyRedirect to="" />} />
        <Route path="journey/:tripId/itinerary" element={<LegacyRedirect to="itinerary" />} />
        <Route path="journey/:tripId/readiness" element={<LegacyRedirect to="readiness" />} />
        <Route path="experience/:tripId" element={<LegacyRedirect to="" />} />
        <Route path="experience/:tripId/itinerary" element={<LegacyRedirect to="itinerary" />} />
        <Route path="experience/:tripId/readiness" element={<LegacyRedirect to="readiness" />} />
        {/* The pre-Phase-2 TripHome remains compiled for one cycle so
            anyone with a bookmark to /trip-home/:id (admin support
            links etc.) can still reach it. */}
        <Route path="trip-home/:tripId" element={<ClientTripHome />} />

        {/* Documents / Travelers / Payments — Phase 1a removes these
            from the top-level sidebar; their routes stay reachable so
            existing deep links and email CTAs still work. Phase 2 will
            absorb them as tabs inside an Experience. */}
        <Route path="documents" element={<ClientDocuments />} />
        <Route path="travelers" element={<ClientTravelers />} />
        <Route path="payments" element={<ClientPayments />} />

        {/* Support */}
        <Route path="messages" element={<ClientConcierge />} />
        <Route path="concierge" element={<Navigate to="/portal/client/messages" replace />} />
        <Route path="connect" element={<ClientConnect />} />
        <Route path="schedule" element={<Navigate to="/portal/client/connect?mode=schedule" replace />} />

        {/* Account */}
        <Route path="settings" element={<ClientSettings />} />
        <Route path="settings/notifications" element={<NotificationsSettings />} />

        {/* Old Journey landing page — kept compiled for a session, removable next pass */}
        <Route path="all-trips" element={<ClientJourney />} />

        <Route path="*" element={<Navigate to="/portal/client" replace />} />
      </Route>
    </Routes>
  );
}
